import { Divider } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import querystring from 'query-string';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import escapesAdImage from '@/assets/advertisement-tile/escapes-ad.jpg';
import fortWildernessAdImage from '@/assets/advertisement-tile/fort-wilderness-ad.png';
import odnAdImage from '@/assets/advertisement-tile/odn-ad.jpg';
import Fade from '@/components/common/Transition/Fade';
import NoResultsMessage from '@/components/route/search/AlertMessage/NoResultsMessage';
import OwnerResultsMessage from '@/components/route/search/AlertMessage/OwnerResultsMessage';
import VehicleNoResultsMessage from '@/components/route/search/AlertMessage/VehicleNoResultsMessage';
import { useSearchResultsCtx } from '@/components/route/search/SearchResults/SearchResults.context';
import Heading from '@/components/switchback/Heading/Heading';
import Link from '@/components/switchback/Link/Link';
import { EListingTileVariant, ListingTile } from '@/components/ui/ListingTile';
import { HILL_COUNTRY_ODN_ID } from '@/constants/campgroundODN';
import { ESearchFilters } from '@/constants/searchFilters';
import { RV_CAMPSITE_BUNDLES_LANDING_PAGE } from '@/constants/urls';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import { useNearFortWilderness } from '@/hooks/useNearLocation';
import useQueryParams from '@/hooks/useQueryParams';
import { getQueryParams } from '@/redux/selectors/queryParams';
import {
  getIsLoading,
  isCampgroundSearchResult,
  TSearchResult,
} from '@/redux/selectors/search/searchResults';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import {
  ERentalCategory,
  INearbyCampgrounds,
  INearbyCampgroundsForDelivery,
} from '@/services/types/search/rentals/id';
import { isProdDeploy } from '@/utility/isSSR';
import {
  ICampgroundTile,
  IRentalTile,
  isCampgroundTile,
  ISearchResultTile,
} from '@/utility/mapSearchResultToTile';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

import { AdvertisementTile } from '../AdvertisementTile/AdvertisementTile';
import { AdvertisementTileV2 } from '../AdvertisementTileV2/AdvertisementTileV2';
import { AdvertisementTileV3 } from '../AdvertisementTileV3/AdvertisementTileV3';
import PaginationContainer from '../PaginationContainer/PaginationContainer';
import { NearbyResultsCountContainer } from '../ResultsCount/ResultsCountContainer';
import { PriceTransparencyPopover } from './PriceTransparencyPopover';
import { useStationaryCamperPromo } from './useStationaryCamperPromo';

type ISearchResultsElement = React.HTMLAttributes<HTMLElement>;

interface ITileProps {
  isMapOpen?: boolean;
  wishlistingEventData?: IWishlistingEventData;
  onClickRental: (rental: ISearchResultTile, index: number, quickPreview?: boolean) => void;
  onChangeImage: (rental: ISearchResultTile, index: number, nextIndex: number) => void;
}

interface IProps extends ITileProps {
  ownerName?: string;
  searchResult: TSearchResult;
  onOwnerButtonClick: () => void;
  isStayTab?: boolean;
  isCampgroundTab?: boolean;
  isPromoPage?: boolean;
  isEscapesSearchPage?: boolean;
  isCampgroundsSearchPage?: boolean;
  isNewPromoPage?: boolean;
}

interface IResultsProps extends ITileProps {
  hasResults?: boolean;
  rentals?: IRentalTile[] | ICampgroundTile[];
  isStayTab?: boolean;
  isCampgroundTab?: boolean;
  isPromoPage?: boolean;
  showStationaryCamperPromo?: boolean;
  shouldShowProBadge?: boolean;
  nearbyCampgroundsForDelivery?: INearbyCampgroundsForDelivery | null;
  nearbyCampgrounds?: INearbyCampgrounds | null;
  nearbyCampgroundsForEscapes?: INearbyCampgrounds | null;
  isNearby?: boolean;
  isEscapesSearchPage?: boolean;
  isCampgroundsSearchPage?: boolean;
  isRvDeliverySearchPage?: boolean;
}

const TilesGrid: React.FC<IResultsProps> = ({
  rentals,
  isMapOpen,
  onClickRental,
  onChangeImage,
  isPromoPage = false,
  showStationaryCamperPromo = false,
  shouldShowProBadge = false,
  nearbyCampgrounds = null,
  nearbyCampgroundsForEscapes = null,
  wishlistingEventData,
  isNearby = false,
  isEscapesSearchPage = false,
}) => {
  const intl = useIntl();
  const { isLocalityPage } = useIsLocalityPage();

  const { isAboveXL, isAboveDesktop, isMobile } = useBreakpoint();
  const { hoveredId, mapHoveredId, updateHoveredId } = useSearchResultsCtx();
  const filtersFromQuery = useSelector(getQueryParams);
  const isNearFortWilderness = useNearFortWilderness();
  const rentalCategoryFromQuery =
    ((filtersFromQuery && filtersFromQuery[ESearchFilters.RENTAL_CATEGORY]) as ERentalCategory) ||
    ERentalCategory.RV;
  const isRv = rentalCategoryFromQuery === ERentalCategory.RV;
  const escapesAdsExperiment = useExperimentIsEnabled(OptimizelyFlags.ESCAPES_ADS);
  const listingQuickPreviewExperiment = useExperimentIsEnabled(
    OptimizelyFlags.LISTING_QUICK_PREVIEW,
  );

  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const isDeliveryMode = isDeliveryModeFromParams(filtersFromQuery);

  const handleMouseEnter = (rental: ISearchResultTile) => updateHoveredId(rental.id || null);
  const { stationaryCamperRentalId, customRentalTitle, hasStationaryCamperInFirstSix } =
    useStationaryCamperPromo(rentals);

  const handleMouseLeave = () => updateHoveredId(null);

  const outdoorsyStaysExperimentEnabled = useExperimentIsEnabled(OptimizelyFlags.OUTDOORSY_STAYS);
  const deliveryStationaryFromQuery = filtersFromQuery[ESearchFilters.DELIVERY_STATIONARY];

  const superhostBadgeEnabled = useExperimentIsEnabled(OptimizelyFlags.SUPERHOST_BADGE);

  const showAdvertisementTile =
    outdoorsyStaysExperimentEnabled && isRv && !deliveryStationaryFromQuery;
  const AdvertisementTileTarget = isAboveDesktop ? '_blank' : undefined;

  const hillCountryAdvertisement = useMemo(() => {
    if (!showAdvertisementTile) return null;

    if (nearbyCampgrounds?.data?.some(campground => campground.odn_id === HILL_COUNTRY_ODN_ID)) {
      return {
        heading: intl.formatMessage({
          defaultMessage: 'Discover luxury glamping in the heart of Texas Hill Country',
          id: 'JMP8CJ',
        }),
        linkLabel: intl.formatMessage({ defaultMessage: 'Book a stay', id: 'hakMYM' }),
        image: odnAdImage.src,
        href: '/destinations/outdoorsy-hill-country',
        odn_id: HILL_COUNTRY_ODN_ID,
        target: AdvertisementTileTarget,
      };
    }
  }, [AdvertisementTileTarget, intl, nearbyCampgrounds?.data, showAdvertisementTile]);

  const fortWildernessAdvertisement = useMemo(() => {
    if (!showAdvertisementTile || !isNearFortWilderness || isPromoPage || isProdDeploy())
      return null;

    return {
      heading: intl.formatMessage(
        {
          defaultMessage: 'Get an RV delivered to Disney’s {br} Fort Wilderness Resort',
          id: 'WdnFjN',
        },
        { br: <br /> },
      ),
      linkLabel: intl.formatMessage({ defaultMessage: 'Learn more ', id: 'cQ/nNz' }),
      image: fortWildernessAdImage.src,
      gradient: true,
      href: '/promos/rv-delivery/fort-wilderness',
      target: AdvertisementTileTarget,
    };
  }, [AdvertisementTileTarget, intl, isNearFortWilderness, isPromoPage, showAdvertisementTile]);

  const escapesAdvertisement = useMemo(() => {
    if (!showAdvertisementTile || hillCountryAdvertisement || fortWildernessAdvertisement)
      return null;

    if (nearbyCampgroundsForEscapes?.data.length) {
      const path = `${RV_CAMPSITE_BUNDLES_LANDING_PAGE}/${nearbyCampgroundsForEscapes?.data[0]?.vacation_package_slug?.split('/').pop()}`;
      return {
        title: intl.formatMessage({
          defaultMessage: 'Outdoorsy Escapes',
          id: 'TiSYoJ',
        }),
        heading: intl.formatMessage({
          defaultMessage: 'RV Delivery to resort destinations',
          id: '6rcfGU',
        }),
        promoText: intl.formatMessage({
          defaultMessage: 'NEW!',
          id: '8QO8e2',
        }),
        linkLabel: intl.formatMessage({ defaultMessage: 'Book now', id: '1mLe+3' }),
        image: escapesAdImage.src,
        href: path,
        gradient: true,
        target: AdvertisementTileTarget,
      };
    }
  }, [
    AdvertisementTileTarget,
    fortWildernessAdvertisement,
    hillCountryAdvertisement,
    intl,
    nearbyCampgroundsForEscapes?.data,
    showAdvertisementTile,
  ]);

  if (!rentals?.length) return null;

  return (
    <ul
      className={clsx('grid gap-5 grid-cols-1 sm:grid-cols-2 mb-11 lg:mb-12', {
        'lg:grid-cols-3': !isEscapesSearchPage,
        'lg:grid-cols-2': isEscapesSearchPage,
      })}
      data-map-open={String(isMapOpen || false)}>
      {hillCountryAdvertisement && !isEscapesSearchPage && (
        <AdvertisementTile {...hillCountryAdvertisement} />
      )}
      {fortWildernessAdvertisement && !isEscapesSearchPage && (
        <AdvertisementTileV2 {...fortWildernessAdvertisement} />
      )}
      {escapesAdvertisement && !isEscapesSearchPage && (
        <AdvertisementTileV3 {...escapesAdvertisement} showBanner={escapesAdsExperiment} />
      )}
      {rentals?.map((tile, index) => {
        const displayStationaryCamperPromo =
          tile.id === stationaryCamperRentalId &&
          hasStationaryCamperInFirstSix &&
          showStationaryCamperPromo;

        const tileWidget = isCampgroundTile(tile) ? (
          <ListingTile
            rentalTile={tile}
            isOutlined={mapHoveredId === tile.id || hoveredId === tile.id}
            onSlideChange={nextIndex => onChangeImage(tile, index, nextIndex)}
            target={isAboveDesktop ? '_blank' : undefined}
            onClick={() => onClickRental(tile, index)}
            onMouseEnter={() => handleMouseEnter(tile)}
            onMouseLeave={() => handleMouseLeave()}
            withPriority={!isMobile || (isMobile && index < 2)}
            withFetchPriority={isMobile && index < 2 ? 'high' : undefined}
            ssrNavigation={false}
            wishlistingEventData={{ index, ...wishlistingEventData }}
            isEscapesSearchPage={isEscapesSearchPage}
          />
        ) : (
          <ListingTile
            rentalTile={tile}
            isOutlined={mapHoveredId === tile.id || hoveredId === tile.id}
            onSlideChange={nextIndex => onChangeImage(tile, index, nextIndex)}
            target={isAboveDesktop ? '_blank' : undefined}
            onClick={() => onClickRental(tile, index)}
            onMouseEnter={() => handleMouseEnter(tile)}
            onMouseLeave={() => handleMouseLeave()}
            dataTestId="search-result"
            displayStationaryCamperPromo={displayStationaryCamperPromo}
            customRentalTitle={customRentalTitle || tile.title}
            variant={EListingTileVariant.Vertical}
            showSuperhost={superhostBadgeEnabled}
            showGuestFavorite
            showProBadge={shouldShowProBadge && tile.dealer}
            withPriority={!isMobile || (isMobile && index < 2)}
            withFetchPriority={isMobile && index < 2 ? 'high' : undefined}
            ssrNavigation={false}
            wishlistingEventData={{ index, ...wishlistingEventData }}
            showQuickPreview={listingQuickPreviewExperiment}
            quickPreviewOnClickViewListing={() => onClickRental(tile, index, true)}
          />
        );

        return (
          <li key={tile.id || index}>
            {/*
              Performance Note:
              Wrapping tiles in <Fade /> can delay visual feedback on page load, especially under throttled conditions.
              This animation has been linked to increased INP scores, particularly on locality pages.
              To improve perceived performance and reduce INP, avoid using <Fade /> when possible—especially on initial render.
            */}
            {!isLocalityPage ? <Fade duration={150}>{tileWidget}</Fade> : tileWidget}
            {index === 0 &&
              !tile.loading &&
              !isNearby &&
              !isEscapesSearchPage &&
              isRentalsVDeliverySearchModeEnabled &&
              !isDeliveryMode && <PriceTransparencyPopover isMapOpen={isMapOpen && !isAboveXL} />}
          </li>
        );
      })}
    </ul>
  );
};

const NoSearchResults: React.FC<IResultsProps> = ({
  hasResults,
  isMapOpen,
  rentals,
  onClickRental,
  onChangeImage,
  isStayTab,
  isCampgroundTab,
  isCampgroundsSearchPage,
}) => {
  const intl = useIntl();
  const queryParams = useQueryParams();
  const showMoreRVsText = intl.formatMessage({
    defaultMessage: 'Show more RVs',
    id: 'hsUwP6',
  });
  const showMoreDeliverableRVsText = intl.formatMessage({
    defaultMessage: 'Show more deliverable RVs',
    id: '43hBvv',
  });

  const similarQuery = {
    [ESearchFilters.ADDRESS]: queryParams[ESearchFilters.ADDRESS],
    [ESearchFilters.DELIVERY_ADDRESS]: queryParams[ESearchFilters.DELIVERY_ADDRESS],
    [ESearchFilters.BOUNDS_NE]: queryParams[ESearchFilters.BOUNDS_NE],
    [ESearchFilters.BOUNDS_SW]: queryParams[ESearchFilters.BOUNDS_SW],
  };

  const deliveryQuery = {
    [ESearchFilters.DELIVERY]: 'true',
    ...similarQuery,
  };

  const isStayOrCampgroundsTab = isStayTab || isCampgroundTab;

  const ShowMoreRVsLink = !rentals?.length ? null : (
    <Link
      href={`/rv-search?${querystring.stringify(isStayTab ? deliveryQuery : similarQuery)}`}
      className="block mb-7">
      <span className="text-base font-medium autoType500">
        {isStayOrCampgroundsTab ? showMoreDeliverableRVsText : showMoreRVsText}
      </span>
    </Link>
  );

  const NoResultsMsg = hasResults ? null : isStayOrCampgroundsTab ? (
    <NoResultsMessage
      hasNearby={!!rentals?.length}
      isStay={isStayTab}
      isCampground={isCampgroundTab}
    />
  ) : (
    <VehicleNoResultsMessage hasNearby={!!rentals?.length} />
  );

  const NearbyResultsHeading =
    !!rentals?.length &&
    (!isStayOrCampgroundsTab ? (
      <>
        <Divider />
        <div className="flex">
          <Heading level={2} className="mt-10 mb-3 highlight autoType600 md:block">
            <FormattedMessage
              defaultMessage="Here are some results that don’t quite match your search criteria"
              id="rNwwki"
              description="Title for nearby listings on search page"
            />
          </Heading>
          <div className="mt-10 mb-3 md:ml-auto">
            <NearbyResultsCountContainer />
          </div>
        </div>
      </>
    ) : (
      <>
        <Heading
          level={2}
          className="mb-4 text-2xl font-medium border-t mt-9 pt-9 border-neutral-20">
          <FormattedMessage
            defaultMessage="Other travel options"
            id="kSKNv4"
            description="Title for nearby listings on search page"
          />
        </Heading>
        <div className="mb-10">
          <p className="text-justify leading-7">
            <FormattedMessage
              defaultMessage={`A delivered RV is a lot like a Stay. You don’t have to drive or tow it. Instead, let your host deliver and set up the vehicle at a campsite you’ve reserved — or a friend’s back yard you’ve called dibs on.`}
              id="0+IcAH"
            />
          </p>
        </div>
      </>
    ));

  const NearbyResultsTileGrid = (
    <TilesGrid
      isMapOpen={isMapOpen}
      rentals={rentals}
      onClickRental={onClickRental}
      onChangeImage={onChangeImage}
      isNearby
    />
  );

  return (
    <>
      {NoResultsMsg}
      {!isCampgroundsSearchPage && NearbyResultsHeading}
      {!isCampgroundsSearchPage && NearbyResultsTileGrid}
      {!isCampgroundsSearchPage && ShowMoreRVsLink}
    </>
  );
};

const SearchResults: React.FC<IProps & ISearchResultsElement> = ({
  searchResult,
  isMapOpen,
  onOwnerButtonClick,
  ownerName,
  onClickRental,
  onChangeImage,
  isStayTab,
  isCampgroundTab,
  isPromoPage,
  wishlistingEventData,
  isEscapesSearchPage,
  isCampgroundsSearchPage,
  isNewPromoPage,
}) => {
  const isLoading = useSelector(getIsLoading);
  const loadingTiles: IRentalTile[] = Array(24).fill({ loading: true });
  const shouldShowProBadge = !isPromoPage && !isEscapesSearchPage;
  const searchResultData = isCampgroundSearchResult(searchResult)
    ? searchResult.campgrounds.data
    : searchResult.data;
  const nearbyCampgrounds = !isEscapesSearchPage ? searchResult.nearbyCampgrounds : null;
  const nearbyCampgroundsForEscapes = searchResult.nearbyCampgroundsForEscapes;

  return (
    <>
      {!!ownerName && !isCampgroundTab && !isNewPromoPage && !isEscapesSearchPage && (
        <OwnerResultsMessage
          ownerName={ownerName}
          onOwnerButtonClick={onOwnerButtonClick}
          isStay={isStayTab}
        />
      )}

      {isLoading ? (
        <TilesGrid
          rentals={loadingTiles}
          isMapOpen={isMapOpen}
          onClickRental={onClickRental}
          onChangeImage={onChangeImage}
          isPromoPage={isPromoPage}
          wishlistingEventData={wishlistingEventData}
          isEscapesSearchPage={isEscapesSearchPage}
        />
      ) : (
        <>
          <TilesGrid
            rentals={searchResultData}
            isMapOpen={isMapOpen}
            onClickRental={onClickRental}
            onChangeImage={onChangeImage}
            isPromoPage={isPromoPage}
            nearbyCampgrounds={nearbyCampgrounds}
            nearbyCampgroundsForEscapes={nearbyCampgroundsForEscapes}
            shouldShowProBadge={shouldShowProBadge}
            showStationaryCamperPromo
            wishlistingEventData={wishlistingEventData}
            isEscapesSearchPage={isEscapesSearchPage}
          />
          {!!searchResult && !isPromoPage && <PaginationContainer />}
          <NoSearchResults
            hasResults={!!searchResultData.length}
            rentals={
              searchResultData.length <= 8 && !isEscapesSearchPage && !isNewPromoPage
                ? searchResult?.nearby?.data
                : []
            }
            isMapOpen={isMapOpen}
            isStayTab={isStayTab}
            isCampgroundTab={isCampgroundTab}
            isCampgroundsSearchPage={isCampgroundsSearchPage}
            onClickRental={onClickRental}
            onChangeImage={onChangeImage}
          />
        </>
      )}
    </>
  );
};

export default SearchResults;
